import {
  headingAnchor,
  isAudioPath,
  isImagePath,
  isVideoPath,
  parseNote,
  type NoteParse,
} from '@rbv/shared';

/** Pure helpers for the editor slice (no DOM, no vault imports — unit-testable). */

// ---------------------------------------------------------------------------
// Media classification
// ---------------------------------------------------------------------------

export type MediaKind = 'image' | 'audio' | 'video' | null;

export function mediaKind(path: string): MediaKind {
  if (isImagePath(path)) return 'image';
  if (isAudioPath(path)) return 'audio';
  if (isVideoPath(path)) return 'video';
  return null;
}

// ---------------------------------------------------------------------------
// GFM table parsing (for the rendered table widget)
// ---------------------------------------------------------------------------

export type TableAlign = 'left' | 'center' | 'right' | null;

export interface ParsedTable {
  header: string[];
  align: TableAlign[];
  rows: string[][];
}

/** Split a table line into cells, honoring `\|` escapes. */
export function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '\\' && s[i + 1] === '|') {
      cur += '|';
      i++;
    } else if (ch === '|') {
      cells.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}

const DELIM_CELL = /^:?-+:?$/;

/** Parse a markdown table block (header, delimiter, body rows). Returns null
 * when the text is not a valid table. */
export function parseMarkdownTable(text: string): ParsedTable | null {
  const lines = text.split('\n').filter((l) => l.trim() !== '');
  if (lines.length < 2) return null;
  const header = splitTableRow(lines[0]);
  const delim = splitTableRow(lines[1]);
  if (delim.length === 0 || !delim.every((c) => DELIM_CELL.test(c.replace(/\s/g, '')))) {
    return null;
  }
  const align: TableAlign[] = delim.map((c) => {
    const l = c.startsWith(':');
    const r = c.endsWith(':');
    if (l && r) return 'center';
    if (r) return 'right';
    if (l) return 'left';
    return null;
  });
  const rows = lines.slice(2).map((l) => splitTableRow(l));
  return { header, align, rows };
}

// ---------------------------------------------------------------------------
// Section extraction for ![[note#heading]] / ![[note#^block]] transclusion
// ---------------------------------------------------------------------------

/**
 * Extract the part of `md` referenced by a heading anchor or block id.
 * - heading: from the line after the heading up to (not including) the next
 *   heading of the same or higher level; the heading line itself is kept.
 * - block: the single line carrying the ^id marker (marker stripped).
 * - neither: the body (frontmatter stripped).
 */
export function extractSection(
  md: string,
  opts: { heading?: string; blockId?: string },
  parsed?: NoteParse,
): string {
  const parse = parsed ?? parseNote(md);
  if (opts.blockId) {
    const b = parse.blocks.find((x) => x.id === opts.blockId);
    if (!b) return '';
    const lineStart = md.lastIndexOf('\n', b.start - 1) + 1;
    let lineEnd = md.indexOf('\n', b.start);
    if (lineEnd === -1) lineEnd = md.length;
    return md
      .slice(lineStart, lineEnd)
      .replace(/\s\^[A-Za-z0-9-]+\s*$/, '')
      .trimEnd();
  }
  if (opts.heading) {
    const want = headingAnchor(opts.heading).toLowerCase();
    const idx = parse.headings.findIndex((h) => h.anchor.toLowerCase() === want);
    if (idx === -1) return '';
    const h = parse.headings[idx];
    let end = md.length;
    for (let i = idx + 1; i < parse.headings.length; i++) {
      if (parse.headings[i].level <= h.level) {
        end = parse.headings[i].start;
        break;
      }
    }
    return md.slice(h.start, end).trimEnd();
  }
  return md.slice(parse.bodyStart).trimEnd();
}

// ---------------------------------------------------------------------------
// Task checkbox mapping (reading mode: nth checkbox -> source offset)
// ---------------------------------------------------------------------------

const TASK_LINE = /^(\s*(?:>\s*)*(?:[-*+]|\d+[.)])\s+)\[([ xX])\]\s/;
const FENCE = /^(\s*)(```|~~~)/;

/**
 * Offsets (into `md`) of the state char inside each task marker `[ ]`/`[x]`,
 * in document order, skipping fenced code blocks. Mirrors how remark-gfm
 * finds task list items closely enough for nth-checkbox mapping.
 */
export function taskMarkerOffsets(md: string): number[] {
  const out: number[] = [];
  let offset = 0;
  let inFence = false;
  let fenceMarker = '';
  for (const line of md.split('\n')) {
    const fence = FENCE.exec(line);
    if (fence) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fence[2];
        offset += line.length + 1;
        continue;
      } else if (line.trim().startsWith(fenceMarker)) {
        inFence = false;
        offset += line.length + 1;
        continue;
      }
    }
    if (!inFence) {
      const m = TASK_LINE.exec(line);
      if (m) out.push(offset + m[1].length + 1);
    }
    offset += line.length + 1;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Wikilink display text
// ---------------------------------------------------------------------------

export function wikiDisplayText(w: {
  target: string;
  heading?: string;
  blockId?: string;
  alias?: string;
}): string {
  if (w.alias) return w.alias;
  let s = w.target;
  if (w.heading) s = s ? `${s} › ${w.heading}` : w.heading;
  if (w.blockId) s = s ? `${s} › ^${w.blockId}` : `^${w.blockId}`;
  return s || w.target;
}

/** Stable color pick for the local collab user. */
const USER_COLORS = [
  { color: '#8b7cf6', light: '#8b7cf640' },
  { color: '#f38ba8', light: '#f38ba840' },
  { color: '#a6e3a1', light: '#a6e3a140' },
  { color: '#f9e2af', light: '#f9e2af40' },
  { color: '#8ab4f8', light: '#8ab4f840' },
  { color: '#f5c2e7', light: '#f5c2e740' },
];

export function userColor(clientId: number): { color: string; light: string } {
  return USER_COLORS[Math.abs(clientId) % USER_COLORS.length];
}
